import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import './Settings.css'; // Reuse Settings.css styles for visual consistency

function FinanceApprovalSettings() {
  const [configs, setConfigs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configsRes, rolesRes] = await Promise.all([
        axios.get('/settings/finance-approvals'),
        axios.get('/roles')
      ]);
      setConfigs(configsRes.data?.data || []);
      setRoles(rolesRes.data?.data || []);
    } catch (err) {
      toast.error('Failed to load settings data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (index) => {
    const updated = [...configs];
    updated[index].enabled = !updated[index].enabled;
    setConfigs(updated);
  };

  const handleRoleChange = (index, field, val) => {
    const updated = [...configs];
    updated[index][field] = val;
    setConfigs(updated);
  };

  const handleSave = async (moduleName, config) => {
    if (config.enabled && !config.level1Role) {
      toast.error(`Please select Level 1 Approver Role for ${getModuleLabel(config.module)}`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        module: config.module,
        enabled: config.enabled,
        level1Role: config.level1Role || null,
        level2Role: config.level2Role || null
      };
      await axios.put(`/settings/finance-approvals/${config.module}`, payload);
      toast.success(`${getModuleLabel(config.module)} approval configuration saved`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const getModuleLabel = (module) => {
    switch (module) {
      case 'INCOME': return 'Customer Receipts (Receipts)';
      case 'EXPENSE': return 'Direct / General Expenses';
      case 'PURCHASE': return 'Vendor Purchases';
      case 'PAYMENT': return 'Vendor Payments';
      case 'ADVANCE': return 'Cash Advances';
      default: return module;
    }
  };

  const getModuleIcon = (module) => {
    switch (module) {
      case 'INCOME': return '📥';
      case 'EXPENSE': return '💸';
      case 'PURCHASE': return '🛒';
      case 'PAYMENT': return '📤';
      case 'ADVANCE': return '💵';
      default: return '⚙️';
    }
  };

  if (loading) {
    return (
      <div className="settings-page loading">
        <div className="spinner">Loading Configurations...</div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h2>Finance Approval Settings</h2>
        <p>Configure multi-level approval workflows for transactions across different financial modules.</p>
      </div>

      <div className="settings-note" style={{ marginBottom: '20px' }}>
        <span className="settings-note-icon">ℹ️</span>
        <span>
          If a transaction is created by a user whose role matches either Level 1 or Level 2 approver role, the approval process will be bypassed and the transaction will post immediately.
        </span>
      </div>

      <div className="approval-configs-container">
        {configs.map((config, idx) => (
          <div key={config.module} className="settings-section card" style={{ marginBottom: '25px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>{getModuleIcon(config.module)}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{getModuleLabel(config.module)}</h3>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Module code: {config.module}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontWeight: '500', fontSize: '14px' }}>{config.enabled ? 'Enabled' : 'Disabled'}</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={() => handleToggle(idx)}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>

            {config.enabled && (
              <div className="settings-form-grid" style={{ gap: '20px', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                <div className="form-group">
                  <label>Level 1 Approver Role <span className="required-star">*</span></label>
                  <select
                    className="form-control"
                    value={config.level1Role || ''}
                    onChange={(e) => handleRoleChange(idx, 'level1Role', e.target.value)}
                  >
                    <option value="">-- Select Role --</option>
                    {roles.map(r => (
                      <option key={r.id} value={r.name}>{r.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Level 2 Approver Role (Optional)</label>
                  <select
                    className="form-control"
                    value={config.level2Role || ''}
                    onChange={(e) => handleRoleChange(idx, 'level2Role', e.target.value)}
                  >
                    <option value="">-- None (Single Level Approval) --</option>
                    {roles.map(r => (
                      <option key={r.id} value={r.name}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
              <button
                className="btn btn-primary"
                onClick={() => handleSave(config.module, config)}
                disabled={saving}
              >
                Save {getModuleLabel(config.module).split(' ')[0]} Config
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FinanceApprovalSettings;
